import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Line } from '@ant-design/plots';
import { Card, Empty, Slider, Space } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CHART_COLORS } from '../../../constants/styles';
import { TrendsIntervalSize } from '../../../types/phlebotomist-dashboard';

// Data point for the success rate trend chart
export interface ISuccessRateTrendDataPoint {
  period: string; // e.g., "2024-01-01", "Week 1", "Jan 2024"
  overallSuccessRate: number; // percentage 0-100
  prbcSuccessRate: number; // percentage 0-100
  sglDonorsSuccessRate: number; // percentage 0-100
  dblDonorsSuccessRate: number; // percentage 0-100
}

// Re-export with shorter alias for backward compatibility
export type SuccessRateTrendDataPoint = ISuccessRateTrendDataPoint;

interface ISuccessRateTrendChartProps {
  data: SuccessRateTrendDataPoint[];
  loading?: boolean;
  intervalSize: TrendsIntervalSize;
  /** Container width from parent (for debugging/calculations) */
  containerWidth?: number;
  /** Pane width from parent (for debugging/calculations) */
  paneWidth?: number;
}

// Color scheme for each line - using brand colors
const COLORS = {
  overall: CHART_COLORS.successRates.overall, // Deep navy - Overall Success
  prbc: CHART_COLORS.successRates.prbc, // Brand red - pRBC Rate
  sglDonors: CHART_COLORS.successRates.sglDonors, // Warm beige - SGL Donors
  dblDonors: CHART_COLORS.successRates.dblDonors, // Royal blue - DBL Donors
};

export const SuccessRateTrendChart: FC<ISuccessRateTrendChartProps> = ({
  data,
  loading = false,
  intervalSize,
  paneWidth,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Labels for chart series
  const overallLabel = t('trends.overallSuccessRate', 'Overall Success %');
  const prbcLabel = t('trends.prbcSuccessRate', 'pRBC Rate %');
  const sglDonorsLabel = t('trends.sglDonorsSuccessRate', 'SGL Donors %');
  const dblDonorsLabel = t('trends.dblDonorsSuccessRate', 'DBL Donors %');

  // Visibility state for each series
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  // ============ ZOOM & SCALING ============
  const CHART_PADDING = 200; // Padding for axes, labels, etc. (px)

  // Target pixels per data point for readable density
  const TARGET_PX_PER_POINT = 15; // ~15px per point is readable without being too sparse

  // Calculate initial zoom level based on data point count
  // Goal: Start at a zoom level where data is readable (not too dense)
  const initialZoom = useMemo(() => {
    const availableWidth = (paneWidth ?? 800) - CHART_PADDING;
    const dataPoints = data.length;

    if (dataPoints === 0) return 4;

    // Calculate what width we'd need at zoom=1 to have TARGET_PX_PER_POINT per data point
    const neededWidth = dataPoints * TARGET_PX_PER_POINT;

    // If the available width is already enough, use zoom 1
    if (neededWidth <= availableWidth) return 1;

    // Calculate the multiplier needed: neededWidth / availableWidth
    const neededMultiplier = neededWidth / availableWidth;

    // Reverse the zoom formula: multiplier = 2^((zoom-1)/3.67)
    // So: zoom = 3.67 * log2(multiplier) + 1
    const calculatedZoom = 3.67 * Math.log2(neededMultiplier) + 1;

    // Clamp to valid range [1, 12] and round to nearest integer
    return Math.min(12, Math.max(1, Math.round(calculatedZoom)));
  }, [data.length, paneWidth]);

  // Zoom state: 1-12 scale, starts at calculated initial zoom
  const [zoom, setZoom] = useState<number | null>(null);
  const effectiveZoom = zoom ?? initialZoom;

  // Reset zoom when data length changes (new date range or interval selected)
  useEffect(() => {
    setZoom(null);
  }, [data.length, intervalSize]);

  // Calculate chart width multiplier based on zoom level
  const { chartWidth, needsScroll } = useMemo(() => {
    const multiplier = Math.pow(2, (effectiveZoom - 1) / 3.67);
    const baseWidth = (paneWidth ?? 800) - CHART_PADDING;
    const calculatedWidth = baseWidth * multiplier;

    return {
      chartWidth: calculatedWidth,
      needsScroll: calculatedWidth > baseWidth,
    };
  }, [effectiveZoom, paneWidth]);

  const toggleSeries = (label: string) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  // Transform data for multi-line chart and calculate min/max for auto-scaling
  const { lineData, yDomain } = useMemo(() => {
    const items: { period: string; value: number; type: string }[] = [];
    let minValue = 100;
    let maxValue = 0;

    data.forEach((d) => {
      if (!hiddenSeries.has(overallLabel)) {
        items.push({
          period: d.period,
          value: d.overallSuccessRate,
          type: overallLabel,
        });
        minValue = Math.min(minValue, d.overallSuccessRate);
        maxValue = Math.max(maxValue, d.overallSuccessRate);
      }
      if (!hiddenSeries.has(prbcLabel)) {
        items.push({
          period: d.period,
          value: d.prbcSuccessRate,
          type: prbcLabel,
        });
        minValue = Math.min(minValue, d.prbcSuccessRate);
        maxValue = Math.max(maxValue, d.prbcSuccessRate);
      }
      if (!hiddenSeries.has(sglDonorsLabel)) {
        items.push({
          period: d.period,
          value: d.sglDonorsSuccessRate,
          type: sglDonorsLabel,
        });
        minValue = Math.min(minValue, d.sglDonorsSuccessRate);
        maxValue = Math.max(maxValue, d.sglDonorsSuccessRate);
      }
      if (!hiddenSeries.has(dblDonorsLabel)) {
        items.push({
          period: d.period,
          value: d.dblDonorsSuccessRate,
          type: dblDonorsLabel,
        });
        minValue = Math.min(minValue, d.dblDonorsSuccessRate);
        maxValue = Math.max(maxValue, d.dblDonorsSuccessRate);
      }
    });

    // Add padding to the domain (5% on each side, but floor min to nearest 5)
    const range = maxValue - minValue;
    const padding = Math.max(range * 0.1, 2); // At least 2% padding
    const domainMin = Math.max(0, Math.floor((minValue - padding) / 5) * 5); // Round down to nearest 5
    const domainMax = Math.min(100, Math.ceil((maxValue + padding) / 5) * 5); // Round up to nearest 5, cap at 100

    return {
      lineData: items,
      yDomain: [domainMin, domainMax] as [number, number],
    };
  }, [
    data,
    hiddenSeries,
    overallLabel,
    prbcLabel,
    sglDonorsLabel,
    dblDonorsLabel,
  ]);

  // Performance: disable animations for large datasets
  const isLargeDataset = data.length > 50;

  // Line chart config
  const config = useMemo(
    () => ({
      data: lineData,
      xField: 'period',
      yField: 'value',
      colorField: 'type',
      legend: false,
      animate: !isLargeDataset,
      shapeField: 'smooth',
      // Remove internal padding so first/last points align with edges
      inset: 0,
      scale: {
        x: { padding: 0.1 },
        y: { domain: yDomain },
        color: {
          domain: [overallLabel, prbcLabel, sglDonorsLabel, dblDonorsLabel],
          range: [
            COLORS.overall,
            COLORS.prbc,
            COLORS.sglDonors,
            COLORS.dblDonors,
          ],
        },
      },
      style: {
        lineWidth: 4,
      },
      axis: {
        x: {
          label: {
            style: {
              fontWeight: (
                datum: { index: number },
                index: number
              ): number | string => (index % 2 === 0 ? 'bold' : 'normal'),
            },
          },
        },
        y: {
          title: t('trends.successRatePct', 'Success Rate (%)'),
        },
      },
      interaction: isLargeDataset
        ? { elementHighlight: false }
        : { elementHighlight: { background: true } },
    }),
    [
      lineData,
      yDomain,
      overallLabel,
      prbcLabel,
      sglDonorsLabel,
      dblDonorsLabel,
      isLargeDataset,
      t,
    ]
  );

  // Custom legend items
  const legendItems = [
    { label: overallLabel, color: COLORS.overall },
    { label: prbcLabel, color: COLORS.prbc },
    { label: sglDonorsLabel, color: COLORS.sglDonors },
    { label: dblDonorsLabel, color: COLORS.dblDonors },
  ];

  const getIntervalLabel = () => {
    const labels: Record<TrendsIntervalSize, string> = {
      day: t('intervalSize.day', 'Day'),
      week: t('intervalSize.week', 'Week'),
      month: t('intervalSize.month', 'Month'),
      quarter: t('intervalSize.quarter', 'Quarter'),
      year: t('intervalSize.year', 'Year'),
    };
    return labels[intervalSize];
  };

  if (data.length === 0) {
    return (
      <Card
        title={t('trends.successRatesTitle', 'Success Rates - Trends')}
        loading={loading}
      >
        <Empty description={t('trends.noData', 'No data available')} />
      </Card>
    );
  }

  return (
    <Card
      title={`${t('trends.successRatesTitle', 'Success Rates - Trends')} (${t(
        'trends.by',
        'by'
      )} ${getIntervalLabel()})`}
      loading={loading}
      style={{ position: 'relative' }}
    >
      {/* Custom Legend - clickable to toggle visibility */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
          justifyContent: 'center',
        }}
      >
        {legendItems.map((item) => {
          const isHidden = hiddenSeries.has(item.label);
          return (
            <button
              key={item.label}
              type="button"
              onClick={() => toggleSeries(item.label)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                background: isHidden ? '#f5f5f5' : '#fff',
                cursor: 'pointer',
                opacity: isHidden ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              {/* Line icon for all series */}
              <span
                style={{
                  display: 'inline-block',
                  width: 16,
                  height: 3,
                  backgroundColor: isHidden ? '#d9d9d9' : item.color,
                  borderRadius: 1,
                  transition: 'background-color 0.2s',
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  textDecoration: isHidden ? 'line-through' : 'none',
                  color: isHidden ? '#999' : '#333',
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Zoom control */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginBottom: 8,
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, color: '#999' }}>
          {t('trends.zoomLabel', 'Zoom')}:
        </span>
        <Space size="small">
          <ZoomOutOutlined style={{ color: '#999' }} />
          <Slider
            min={1}
            max={12}
            step={1}
            value={effectiveZoom}
            onChange={setZoom}
            style={{ width: 150 }}
            tooltip={{
              formatter: (val) => `Level ${val}`,
            }}
          />
          <ZoomInOutlined style={{ color: '#999' }} />
        </Space>
      </div>

      {/* Scrollable chart container */}
      <div
        style={{
          overflowX: needsScroll ? 'auto' : 'hidden',
          overflowY: 'hidden',
        }}
      >
        <div
          style={{
            width: chartWidth,
            minWidth: chartWidth,
          }}
        >
          <Line {...config} height={650} width={chartWidth} />
        </div>
      </div>
    </Card>
  );
};

export default SuccessRateTrendChart;
